import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, XCircle, PauseCircle, FileText, RotateCw, ShieldAlert, Mail, Send } from "lucide-react";
import { STAGE_LABEL } from "@/lib/statusMap";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/dateFmt";

const ACTION_META = {
  submitted:                    { icon: FileText,     color: "text-[#2d6b6b]" },
  resubmitted:                  { icon: RotateCw,     color: "text-[#2d6b6b]" },
  approved:                     { icon: CheckCircle2, color: "text-emerald-700" },
  approve:                      { icon: CheckCircle2, color: "text-emerald-700" },
  on_hold:                      { icon: PauseCircle,  color: "text-yellow-700" },
  reject:                       { icon: XCircle,      color: "text-[#b3261e]" },
  rejected:                     { icon: XCircle,      color: "text-[#b3261e]" },
  update_status:                { icon: Clock,        color: "text-indigo-700" },
  mark_fulfilled:               { icon: CheckCircle2, color: "text-[#2d6b6b]", label: "Marked fulfilled" },
  parent_payment_email_sent:    { icon: Mail,         color: "text-[#7a1f2b]", label: "Parent payment email sent" },
  parent_payment_email_resent:  { icon: Send,         color: "text-[#7a1f2b]", label: "Parent payment email resent" },
  super_admin_edit:             { icon: ShieldAlert,  color: "text-[#b3261e]", loud: true, label: "Super Admin edit" },
  super_admin_reassignment:     { icon: ShieldAlert,  color: "text-[#b3261e]", loud: true, label: "Super Admin reassignment" },
  super_admin_stage_override:   { icon: ShieldAlert,  color: "text-[#b3261e]", loud: true, label: "Super Admin stage override" },
  super_admin_delete:           { icon: ShieldAlert,  color: "text-[#b3261e]", loud: true, label: "Super Admin delete" },
  super_admin_restore:          { icon: ShieldAlert,  color: "text-[#b3261e]", loud: true, label: "Super Admin restore" },
};

const ACCOUNTS_PO_EMAIL = "sangita.k@mitgurukul.com";
const IS_PAYMENT_EMAIL_ACTION = (a) =>
  a === "parent_payment_email_sent" || a === "parent_payment_email_resent";

function ResendButton({ entry, refCode, onDone }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const canResend =
    user &&
    (user.role === "super_admin" || user.email?.toLowerCase() === ACCOUNTS_PO_EMAIL);
  if (!canResend) return null;
  const to = entry.to || "the parent";
  const click = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/requisitions/${refCode}/resend-parent-payment`,
        {},
      );
      toast.success(`Resent payment details to ${data.to}`);
      onDone?.(data);
    } catch (err) {
      toast.error(formatApiError(err), { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={click}
      disabled={busy}
      className={`ml-2 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold border shadow-sm bg-[#7a1f2b] hover:bg-[#5a1520] text-white border-[#5a1520] ${busy ? "opacity-50 cursor-not-allowed" : ""}`}
      data-testid={`resend-parent-payment-btn`}
      title={`Resend the same email to ${to}`}
    >
      {busy
        ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
        : <Send size={11} />}
      {busy ? "Resending…" : `Resend to ${to}`}
    </button>
  );
}

export default function AuditTrail({ entries = [], refCode, onResent }) {
  if (!entries.length) {
    return <div className="text-sm text-[#6e7770]">No history yet.</div>;
  }
  return (
    <ol className="relative border-l-2 border-[#e2d7c3] ml-2" data-testid="audit-trail">
      {entries.map((e, i) => {
        const meta = ACTION_META[e.action] || { icon: FileText, color: "text-[#7a1f2b]" };
        const Icon = meta.icon;
        const loud = meta.loud;
        return (
          <li key={i} className="mb-6 ml-6" data-testid={`audit-entry-${i}`}>
            <span className={`absolute -left-3.5 flex items-center justify-center w-7 h-7 rounded-full border shadow-sm ${
              loud ? "bg-[#fcebe9] border-[#b3261e]/50" : "bg-white border-[#e2d7c3]"
            }`}>
              <Icon size={16} className={meta.color} />
            </span>
            <div className={`flex flex-wrap items-baseline gap-x-2 ${loud ? "text-[#b3261e]" : ""}`}>
              <span className="font-serif font-semibold text-[#1f2320]">{e.actor_name}</span>
              <span className="text-xs text-[#6e7770]">({e.actor_role})</span>
              {loud && <span className="badge bg-[#b3261e] text-white border-[#b3261e]">Override</span>}
              <span className="text-xs text-[#6e7770] ml-auto">{formatDate(e.at, { includeTime: true })}</span>
            </div>
            <div className={`text-sm font-medium mt-0.5 ${loud ? "text-[#b3261e]" : "text-[#2d6b6b]"}`}>
              <span className="capitalize">{(meta.label || e.action).replace(/_/g, " ")}</span>
              {" · "}
              <span className="text-[#4a524c]">Stage: {STAGE_LABEL[e.stage] || e.stage}</span>
              {e.sub_status && <span className="text-[#4a524c]"> · Sub-status: {e.sub_status}</span>}
              {e.acted_on_behalf_of && (
                <span className="text-[#4a524c]"> · Acting on behalf of {STAGE_LABEL[e.acted_on_behalf_of] || e.acted_on_behalf_of}</span>
              )}
              {e.reassign_stage && (
                <span className="text-[#4a524c]"> · {e.reassign_stage} → {e.reassign_action || "replace"}</span>
              )}
              {e.from_stage && e.to_stage && (
                <span className="text-[#4a524c]"> · {e.from_stage} → {e.to_stage}</span>
              )}
              {e.changed_keys && e.changed_keys.length > 0 && (
                <span className="text-[#4a524c]"> · fields: {e.changed_keys.join(", ")}</span>
              )}
              {IS_PAYMENT_EMAIL_ACTION(e.action) && e.to && (
                <span className="text-[#4a524c]"> · to {e.to}</span>
              )}
              {IS_PAYMENT_EMAIL_ACTION(e.action) && refCode && (
                <ResendButton entry={e} refCode={refCode} onDone={onResent} />
              )}
            </div>
            {e.remarks && (
              <div className={`mt-1.5 text-sm whitespace-pre-wrap rounded-md p-2.5 ${
                loud
                  ? "bg-[#fcebe9] border border-[#b3261e]/40 text-[#7a1f2b] font-medium"
                  : "text-[#1f2320] bg-[#faf5eb] border border-[#e2d7c3]"
              }`}>
                {loud && <span className="text-[10px] uppercase tracking-widest font-semibold text-[#b3261e] block mb-1">Super Admin override</span>}
                {e.remarks}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
